import React, { useEffect, useState } from 'react';
import { X, MapPin, Users, Lock, MapPinCheck, Camera, Calendar1Icon, FileIcon } from 'lucide-react';
import { themeStore } from '../../../stores/theme';
import { useStore } from '@tanstack/react-store';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetCompanyByIdQuery } from '../../../hooks/useAuth';
import { useGetEventQuery } from "../../../hooks/useEvents"
import { useGetLocationQuery } from "../../../hooks/useLocations"
import { useSubscriptionPackageDetailQuery, useMakePaymentEvent, useCompanyPaymentResourceListQuery, useUpgradePlan } from "../../../hooks/useSubscriptions"
import eventIcon from "../../../assets/icons/calendar.png"
import StripePaymentBox from '../../common/StripePaymentBox';
import Modal from '../../common/ModalComponent';
import toast from 'react-hot-toast';
import { sec1 } from '../../ui/media';

const MakePayment = () => {
  const { REACT_APP_IMAGE_URL } = process.env
  const { isDarkMode } = useStore(themeStore)
  const { _id, user_id, type, entity, actionType, prod_id } = useParams()
  const { data: userDataResponse, isSuccess: userIsSuccess } = useGetCompanyByIdQuery(`?company_id=${user_id}`)
  const { data: paymentResourceResponse, isSuccess: paymentResourceIsSuccess } = useCompanyPaymentResourceListQuery(`?company_id=${user_id}`)
  const [userData, setUserData] = useState(null)
  const [entityData, setEntityData] = useState(null)
  const [packageData, setPackageData] = useState(null)
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [modalTitle, setModalTitle] = useState(false);
  const [modalMessage, setModalMessage] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [paymentId, setPaymentId] = useState(null);
  const [finalAmount, setFinalAmount] = useState(0);
  const navigate = useNavigate()

  const makePaymentMutation = useMakePaymentEvent()
  const upgradePlanMutation = useUpgradePlan()
  const { data: locationResponse, isSuccess: isLocationSuccess } = useGetLocationQuery(`?location_id=${_id}`, entity)
  const { data: eventResponse, isSuccess: isEventSuccess } = useGetEventQuery(`?event_id=${_id}`, entity)
  let response, success;
  if (entity == "location") {
    response = locationResponse;
    success = isLocationSuccess;
  } else if (entity == "event") {
    response = eventResponse;
    success = isEventSuccess;
  }
  const { data: packageResponse, isSuccess: packageIsSuccess } = useSubscriptionPackageDetailQuery(
    success && prod_id
      ? `?product_id=${prod_id}`
      : null,
    {
      enabled: !!(success && entityData?.product_id),
    }
  );

  // Theme colors
  const colors = {
    background: isDarkMode ? '#121212' : '#f5f7fa',
    card: isDarkMode ? '#1e1e1e' : '#ffffff',
    text: isDarkMode ? '#e0e0e0' : '#333333',
    border: isDarkMode ? '#333333' : '#e0e0e0'
  };

  const handlePayment = async () => {
    const amount = packageData?.price_value
    const product_type = userData?.user_type == 2 ? "event" : "location"
    const product_id = packageData?.id
    const body = {
      user_id,
      product_type,
      product_id,
      total_amount: amount
    }
    if (entity == "location") {
      body.location_id = _id || entityData?._id
    } else if (entity == "event") {
      body.event_id = _id || entityData?._id
    }
    if (actionType == "upgrade") {
      upgradePlanMutation.mutate(body, {
        onSuccess: ((data) => {
          setPaymentId(data?.data?.data?.client_secret)
          setFinalAmount(data?.data?.data?.payment ?? 0)
        }),
        onError: (err) => {
          const message = err.response?.data?.message || "Payment Failed"
          setShowResponseModal(true)
          setModalTitle("Payment failed")
          setModalMessage(message || "Payment not initialised !!! Please contact to support")
        }
      })
    } else if (actionType == "purchase") {
      makePaymentMutation.mutate(body, {
        onSuccess: ((data) => {
          setPaymentId(data?.data?.data?.client_secret)
        }),
        onError: (err) => {
          const message = err.response?.data?.message || "Payment Failed"
          setShowResponseModal(true)
          setModalTitle("Payment failed")
          setModalMessage(message || "Payment not initialised !!! Please contact to support")
        }
      })
    } else {
      toast.error("URL Tampered")
      navigate(-1)
    }
  }

  const payResponse = (response) => {
    setShowResponseModal(true)
    setModalTitle(response?.title)
    setModalMessage(response?.message)
  }

  const handleOk = () => {
    setShowResponseModal(false);
    if (type == "internal") {
      if (entity == "event") {
        navigate('/events/list')
      } else if (entity == "location") {
        navigate('/locations/list')
      } else {
        navigate('/lander')
      }
    } else {
      navigate('/lander')
    }
  }

  const cardIcons = [
    { name: 'Visa', src: 'https://img.icons8.com/color/48/visa.png' },
    { name: 'Mastercard', src: 'https://img.icons8.com/color/48/mastercard.png' },
    { name: 'American Express', src: 'https://img.icons8.com/color/48/amex.png' },
    { name: 'Discover', src: 'https://img.icons8.com/color/48/discover.png' },
    { name: 'Diners Club', src: 'https://img.icons8.com/color/48/diners-club.png' },
    { name: 'JCB', src: 'https://img.icons8.com/color/48/jcb.png' },
    { name: 'China UnionPay', src: 'https://img.icons8.com/color/48/unionpay.png' }
  ];

  useEffect(() => {
    if (userIsSuccess) {
      setUserData(userDataResponse?.data?.data)
    }
  }, [userDataResponse, userIsSuccess])

  useEffect(() => {
    if (success) {
      setEntityData(response?.data?.data)
    }
  }, [response, success])

  useEffect(() => {
    if (packageIsSuccess) {
      setPackageData(packageResponse?.data?.data)
    }
  }, [packageResponse, packageIsSuccess])

  useEffect(() => {
    if (paymentResourceIsSuccess) {
      setSavedCards(paymentResourceResponse?.data?.data)
    }
  }, [paymentResourceResponse, paymentResourceIsSuccess])

  useEffect(() => {
    if (userData && packageData) {
      handlePayment();
    }
  }, [userData, packageData]);

  return (
    <>
      <div className="flex justify-center items-center p-4 min-h-screen" style={{ background: `transparent` }} >
        <div className="w-full max-w-[80%] rounded-xl overflow-hidden shadow-lg" style={{ backgroundColor: colors.card }}>
          {/* Header */}
          <div className={`p-4 flex justify-between items-center ${isDarkMode ? "bg-blue-800" : "bg-blue-100"}`}>
            <h1 className="text-xl poppins-regular text-center flex-grow" style={{ color: colors.text }}>{entityData?.name} Detail Screen</h1>
          </div>

          <div className="flex flex-col md:flex-row p-6 pb-2 gap-6">
            <div className="flex-1">
              <h2 className="text-lg poppins-regular mb-4" style={{ color: colors.text }}>{entity == "location" ? "Location" : "Event"} details</h2>
              <div className="h-[425px] mb-6 rounded-lg border overflow-hidden" style={{ borderColor: colors.border }}>
                <div className="h-48 bg-gray-200 relative p-4">
                  {entityData?.media != "" ?
                    <img
                      src={`${REACT_APP_IMAGE_URL}/${entityData?.media}`}
                      alt="Concert"
                      className="h-full object-cover"
                    />
                    : entity == "event" ?
                      <img
                        src={eventIcon}
                        alt="Concert"
                        className="h-full object-cover"
                      />
                      : <MapPin size={100} />
                  }
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="items-center">
                      <p style={{ color: colors.text }} className='text-[16px]'>{entity == "location" ? "Location Name" : "Event Name"}</p>
                      <div className="flex items-center space-x-1 mt-1">
                        <FileIcon className="w-5 h-5 text-blue-500" />
                        <span className="font-medium" style={{ color: colors.text }}>{entityData?.name}</span>
                      </div>
                    </div>
                    {entity != "location" && <div className="items-center">
                      <p style={{ color: colors.text }} className='text-[16px]'>Event Date</p>
                      <div className="flex items-center space-x-1 mt-1">
                        <Calendar1Icon className="w-5 h-5 text-blue-500" />
                        <span className={`${isDarkMode ? "text-white" : "text-gray-600"} text-[15px]`}>{entityData?.date}</span>
                      </div>
                    </div>}
                    <div className="items-center">
                      <p style={{ color: colors.text }} className='text-[16px]'>Access Code</p>
                      <div className="flex items-center space-x-1 mt-1">
                        <Lock className="w-5 h-5 text-blue-500" />
                        <span className="text-sm" style={{ color: colors.text }}>
                          {(entityData?.payment_status == 2 || entityData?.payment_status == 5) ? entityData?.access_code : "******"}
                        </span>
                      </div>
                    </div>
                    <div className="items-center">
                      <p style={{ color: colors.text }} className='text-[16px]'>{entity == "location" ? "Camera Limit" : "Users Limit"}</p>
                      <div className="flex items-center space-x-1 mt-1">
                        {entity == "location" ? <Camera className="w-5 h-5 text-blue-500" /> : <Users size={18} className="text-blue-500" />}
                        <span className="text-sm" style={{ color: colors.text }}>{entity == "location" ? entityData?.camera_limit : entityData?.user_limit}</span>
                      </div>
                    </div>
                    <div className="items-center">
                      <p style={{ color: colors.text }} className='text-[16px]'>{entity == "location" ? "Location" : "Event Address"}</p>
                      <div className="flex items-center space-x-1 mt-1">
                        <MapPin className="w-5 h-5 text-blue-500" />
                        <span className="text-sm" style={{ color: colors.text }}>{entityData?.location?.name}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* right Column - Payment Method */}
            <div className="flex-1">
              <div className="rounded-lg border p-4 mt-11 mb-6" style={{ borderColor: colors.border }}>
                <h2 className="text-lg poppins-regular mb-4" style={{ color: colors.text }}>Payment summary</h2>
                <div className="flex justify-between items-center mb-6">
                  <span className="poppins-regular" style={{ color: colors.text }}>Total</span>
                  <span className="poppins-regular text-xl" style={{ color: colors.text }}>${finalAmount || packageData?.price_value}</span>
                </div>
                <div className="text-sm text-gray-600 mt-1">We accept the following cards:</div>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  {cardIcons.map((card) => (
                    <img
                      key={card.name}
                      src={card.src}
                      alt={card.name}
                      title={card.name}
                      className="h-8 w-auto"
                    />
                  ))}
                </div>
              </div>
              <h2 className="text-lg poppins-regular" style={{ color: colors.text }}>Provide Card Information</h2>
              {paymentId &&
                <StripePaymentBox
                  savedCards={savedCards}
                  clientSecret={paymentId}
                  isDarkMode={isDarkMode}
                  payResponse={payResponse}
                />
              }
            </div>
          </div>
          <div className='text-center mb-6'>
            <button
              onClick={() => handleOk()}
              className="w-[150px] py-2 bg-white border text-blue-600 rounded-full font-medium transition">
              Cancel Payment
            </button>
          </div>
        </div>
      </div>
      <Modal isDarkMode={isDarkMode} key={"paymentResponse"} title={modalTitle} isOpen={showResponseModal} onClose={() => setShowResponseModal(false)}>
        <div className="text-center mb-6">
          <p className="mb-6" style={{ color: colors.text }}>{modalMessage}</p>
          <div className='text-center mt-5'>
            <button
              onClick={() => handleOk()}
              className={`w-[200px] py-3 text-center font-medium ${isDarkMode ? 'bg-white text-gray-900' : 'bg-gray-800 text-white'} transition rounded-full`}>
              OK
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default MakePayment;
